/* eslint-disable max-lines */
import { axios } from "../config/axios";
import { Ijourneys } from "../interfaces/endpoints/Ijourneys";
import { IAxiosResponse } from "../interfaces/IAxiosResponse";
import { IPagination } from "../interfaces/IPagination";
import {
  IAdminPassenger,
  IAdminPassengers,
  IAvailableDates,
  IEditReservationDetails,
  IGetLineId,
  IGetReservationDetails,
  IGetReservationV2,
  ILuggageDetails,
  IReservationCalculation,
  IOpenReturnEntitlement,
  IReservationV2,
  ISelectOpenReturnRequest
} from "../interfaces/reservations/Reservations.types";
import { serialize } from "../utils/serializeToQueryString";

export type getAllTripsParamsType = {
  pagenumber?: number;
};

export const getReservations = async (
  att: getAllTripsParamsType
): Promise<IAxiosResponse<Ijourneys>> => {
  return axios.get<Ijourneys>(`reservation/journeys${serialize(att)}`);
};

//
// NEW
//
export const getReservations_v2 = async ({
  filterByJourneyDate = false,
  ...att
}: IGetReservationV2): Promise<IAxiosResponse<IPagination<IReservationV2>>> => {
  return axios.get(
    `reservation/journeysV2${serialize({ filterByJourneyDate, ...att })}`
  );
};

export const getReservationsDetails = async ({
  uniqueJourneyId
}: IGetReservationDetails): Promise<
  IAxiosResponse<IReservationV2>
> => {
  return axios.get(`Reservation/JourneyReservation/${uniqueJourneyId}`);
};

export const editReservationsDetails = async (
  data: IEditReservationDetails
): Promise<IAxiosResponse<{}>> => {
  return axios.post("Reservation/JourneyReservation", data);
};

export const availableLines = async ({ lineId, reverse }: IGetLineId):
 Promise<IAxiosResponse<IAvailableDates[]>> => {
  return axios.get(`Route/WeeklyAvailableRoutes?lineId=${lineId}&reverse=${reverse}`);
};

// export const editJourneyTicket_v2 = async ({
//   uniqueJourneyId,
//   body
// }: IChangeJourneyTicket): Promise<IAxiosResponse<any>> => {
//   return axios.put(
//     `reservation/changeJourneyTicketV2${serialize({ uniqueJourneyId })}`,
//     { body }
//   );
// };

// export const changeJourneyStatus_v2 = async ({
//   ...att
// }: IChangeJourneyStatus): Promise<IAxiosResponse<any>> => {
//   return axios.put(`reservation/changeJourneyStatusV2${serialize(att)}`);
// };

export const resendJourneyReservationEmail = async (
  uniqueJourneyId: string
) => {
  return axios.get(`Reservation/ResendJourneyReservationEmail/${uniqueJourneyId}`);
};

export const generateJourneyReservationPdf = async (
  uniqueJourneyId: string
) => {
  return axios.get(`Reservation/GenerateJourneyReservationPdf/${uniqueJourneyId}`);
};

// Superadmin trouble-case tool: select the open-return return trip on behalf of
// the customer with no payment (any price difference is comped server-side).
// shouldNotifyUser defaults to true (opt-out) — the customer is told we selected
// the return on their behalf unless the admin unticks it.
export const selectOpenReturnAdmin = async (
  uniqueJourneyId: string,
  body: ISelectOpenReturnRequest,
  shouldNotifyUser = true
): Promise<IAxiosResponse<IOpenReturnEntitlement>> => {
  return axios.post(
    `Reservation/SelectOpenReturn/${uniqueJourneyId}?notify=${shouldNotifyUser}`,
    body
  );
};

// SuperAdmin-only (backend enforces [SuperAdminRoleFilter]): read the luggage storage
// add-on detail for the reservation, shown in the BackOffice luggage modal.
export const getLuggageDetails = async (
  uniqueJourneyId: string
): Promise<IAxiosResponse<ILuggageDetails>> => {
  return axios.get(`Reservation/AdminLuggageDetails/${uniqueJourneyId}`);
};

// SuperAdmin-only (backend enforces [SuperAdminRoleFilter]): edit an existing luggage
// storage add-on's bag count / drop-off date / day count. Works on pending or confirmed
// journeys; the partner booking is cancelled + recreated server-side. No refund/charge —
// the returned calculation reflects the recalculated reservation for display only.
// dropOffDate must be a `yyyy-MM-dd` string (date-only, no timezone).
export const adminEditLuggageStorage = async (
  uniqueJourneyId: string,
  bagCount: number,
  dropOffDate: string,
  dayCount: number
): Promise<IAxiosResponse<IReservationCalculation>> => {
  return axios.post(
    `Reservation/AdminEditLuggageStorage/${uniqueJourneyId}/${bagCount}${serialize({
      dropOffDate,
      dayCount
    })}`
  );
};

// SuperAdmin-only: cancel an existing luggage storage add-on (soft-deletes the mapping and
// cancels the partner booking). No refund is issued. Resolves with `data === "cancelled"`.
export const adminCancelLuggageStorage = async (
  uniqueJourneyId: string
): Promise<IAxiosResponse<string>> => {
  return axios.post(`Reservation/AdminCancelLuggageStorage/${uniqueJourneyId}`);
};

// SuperAdmin-only (backend enforces [SuperAdminRoleFilter]): read the reservation's
// passengers for the BackOffice passenger modal, ordered as they are numbered in the UI.
// The list endpoint's `clientNames` carries no ids, which is why the modal reads from here.
export const getAdminPassengers = async (
  uniqueJourneyId: string
): Promise<IAxiosResponse<IAdminPassengers>> => {
  return axios.get(`Reservation/AdminPassengers/${uniqueJourneyId}`);
};

// SuperAdmin-only: correct passenger first/last names. Names only — passport, age group,
// email, phone and seat assignments are untouched, and the new name is mirrored onto every
// leg server-side. Unlimited: unlike the customer-facing flow there is no Flex check, no edit
// cap and no departure deadline, and it does not consume the customer's Flex edit allowance.
// The ticket email/PDF is NOT resent — use the envelope button on the card for that.
// Resolves with the canonical post-write list, so the modal needs no follow-up GET.
export const adminEditPassengerNames = async (
  uniqueJourneyId: string,
  passengers: IAdminPassenger[]
): Promise<IAxiosResponse<IAdminPassengers>> => {
  return axios.put(`Reservation/AdminEditPassengerNames/${uniqueJourneyId}`, {
    passengers
  });
};
